/**
 * Radio Card Group Component
 *
 * A group of radio-style cards for single selection.
 * Each option displays as a card with label, description, and optional color indicator.
 */

import { cn } from '../../lib/utils';

export interface RadioCardOption<T extends string = string> {
  value: T;
  label: string;
  description?: string;
  /** Optional color indicator */
  color?: string;
  /** Disabled state */
  disabled?: boolean;
}

export interface RadioCardGroupProps<T extends string = string> {
  /** Name attribute for the radio group */
  name: string;
  /** Available options */
  options: RadioCardOption<T>[];
  /** Currently selected value */
  value: T;
  /** Called when selection changes */
  onChange: (value: T) => void;
  /** Optional label for the group */
  label?: string;
  /** Required indicator */
  required?: boolean;
  /** Disabled state for all options */
  disabled?: boolean;
  /** Error message */
  error?: string;
  /** Additional class names */
  className?: string;
  /** Layout direction */
  direction?: 'horizontal' | 'vertical';
}

export function RadioCardGroup<T extends string = string>({
  name,
  options,
  value,
  onChange,
  label,
  required,
  disabled,
  error,
  className,
  direction = 'vertical',
}: RadioCardGroupProps<T>) {
  return (
    <div className={cn('space-y-3', className)}>
      {label && (
        <label className="block text-sm font-medium text-dark-500">
          {label} {required && '*'}
        </label>
      )}

      <div
        className={cn(
          direction === 'horizontal' ? 'flex flex-wrap gap-2' : 'space-y-2'
        )}
      >
        {options.map((option) => {
          const isDisabled = disabled || option.disabled;
          const isSelected = value === option.value;

          return (
            <label
              key={option.value}
              className={cn(
                'flex items-start gap-3 p-3 rounded-lg border transition-all cursor-pointer',
                isSelected
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-neutral-200 hover:border-neutral-300',
                isDisabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={isSelected}
                onChange={() => onChange(option.value)}
                disabled={isDisabled}
                className="mt-1 w-4 h-4 text-primary-500 border-neutral-300 focus:ring-primary-500"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  {option.color && (
                    <span
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: option.color }}
                    />
                  )}
                  <span className="text-sm font-medium text-dark-500">
                    {option.label}
                  </span>
                </div>
                {option.description && (
                  <p className="text-xs text-neutral-500 mt-1">
                    {option.description}
                  </p>
                )}
              </div>
            </label>
          );
        })}
      </div>

      {error && <p className="text-sm text-danger-500">{error}</p>}
    </div>
  );
}
